import { Component, OnInit, Input } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import moment from 'moment';

import {
  IProductType,
  ALLERGENS_STATES,
  IProductAllergen,
  SameDayDeliverySettings,
  hasDailyLimitSoldOutDates,
  IFormattedDeviatingDeadline,
} from '@cakeiteasy/web-utils';

import { getSameDayDeadlineMessage } from '../../../../helpers';

const TABS = {
  ABOUT_PRODUCT: 'ABOUT_PRODUCT',
  INGREDIENTS: 'INGREDIENTS',
  DEADLINE: 'DEADLINE',
  DELIVERY: 'DELIVERY',
};

@Component({
  standalone: false,
  selector: 'cake-info-tabs',
  templateUrl: './cake-info-tabs.component.html',
  styleUrls: ['./cake-info-tabs.component.scss']
})
export class CakeInfoTabsComponent implements OnInit {
  @Input() cake: IProductType;
  @Input() orderDeadlines: any;
  @Input() specialDeadlines: IFormattedDeviatingDeadline[];
  @Input() showBakeryDeadline = true;

  public sameDayDeadlineMessage: string = null;

  public containsMightFreeAllergens = '';
  public mightFreeAllergens = '';
  public ingredients: string;
  public activeTab = TABS.ABOUT_PRODUCT;
  public TABS = TABS;
  public hasDiscount: boolean;
  public discountStr = '';

  public hasDailyLimitSoldOutDates: boolean;
  public isDescriptionTabAvailable: boolean;
  public isIngredientsTabAvailable: boolean;
  public isDeadlineTabAvailable: boolean;

  constructor(private _translate: TranslateService) {
  }

  ngOnInit() {
    const _allergensWithChildren = this._defineAllergensChildren(
      this.cake.allergens
    );
    this._initAllergensStrings(_allergensWithChildren);
    this.hasDiscount = Boolean(this.cake.discount && this.cake.discount.value);

    if (this.cake.ingredients) {
      this.ingredients = this.cake.ingredients.replace(/&nbsp;/gi, ' ');
    }

    if (this.hasDiscount) {
      this._initDiscount();
    }

    this._initTabsInfo();
  }

  private _initTabsInfo() {
    this.sameDayDeadlineMessage = getSameDayDeadlineMessage(this._translate, this.cake);
    this.hasDailyLimitSoldOutDates = hasDailyLimitSoldOutDates(this.cake);

    this.isDescriptionTabAvailable = this.getIsDescriptionTabAvailable() || this.hasDailyLimitSoldOutDates;
    this.isIngredientsTabAvailable = this.getSsIngredientsTabAvailable();
    this.isDeadlineTabAvailable = this.getIsDeadlineTabAvailable();

    this.activeTab = this._getActiveTab();
  }

  public getIsDescriptionTabAvailable(): boolean {
    const { availability, sameDayDeliverySettings } = this.cake;
    const { availableAlways, availablePeriod, unavailablePeriod } = availability;
    const { showDescription, isAvailableForSameDayDelivery } = sameDayDeliverySettings || ({} as SameDayDeliverySettings);

    const hasDescription = Boolean(this.cake.description);
    const hasDiscount = this.hasDiscount;
    const isShowSameDayDeliveryDescription = isAvailableForSameDayDelivery && showDescription;

    const hasUnavailablePeriod = (
      availableAlways &&
      Boolean(unavailablePeriod && unavailablePeriod.length)
    );
    const hasAvailablePeriod = (
      !availableAlways &&
      Boolean(availablePeriod && availablePeriod.begin && availablePeriod.end)
    );

    return (
      hasDescription ||
      hasDiscount ||
      isShowSameDayDeliveryDescription ||
      hasUnavailablePeriod ||
      hasAvailablePeriod
    );
  }

  public getSsIngredientsTabAvailable(): boolean {
    return Boolean(this.containsMightFreeAllergens || this.mightFreeAllergens || this.ingredients);
  }

  public getIsDeadlineTabAvailable(): boolean {
    return Boolean(
      this.showBakeryDeadline &&
      ((this.orderDeadlines && this.orderDeadlines.length) || this.sameDayDeadlineMessage)
    );
  }

  private _getActiveTab() {
    return this.isDescriptionTabAvailable ? TABS.ABOUT_PRODUCT
      : this.isIngredientsTabAvailable ? TABS.INGREDIENTS
        : this.isDeadlineTabAvailable ? TABS.DEADLINE : null;
  }

  private _initDiscount() {
    const { value, expirationDate, infinite } = this.cake.discount;
    this.discountStr =
      expirationDate && !infinite
        ? this._translate.instant('cakeBuilder.productInfo.getDiscountUntil', {
          discount: value,
          date: moment(expirationDate).format('D.M.YY'),
        })
        : this._translate.instant('cakeBuilder.productInfo.getDiscount', {
          discount: value,
        });
  }

  private _initAllergensStrings(allergens: IProductAllergen[]) {
    this.containsMightFreeAllergens = allergens.filter((a) => !a.showAsChild)
      .map((a) => a.name)
      .join(', ');

    this.mightFreeAllergens = this._getMightFreeAllergens(allergens);
  }

  private _getMightFreeAllergens(allergens: IProductAllergen[]): string {
    const isMightFreeAllergen = (state: string) => state === ALLERGENS_STATES.MIGHT_FREE;

    return allergens.filter((allergen: IProductAllergen) => {
      const isMightFree = isMightFreeAllergen(allergen.state);

      if (Boolean(allergen.parentAllergen && isMightFree)) {
        const parent = allergens.find((a: IProductAllergen) => a.id === allergen.parentAllergen);
        return parent ? parent.state !== ALLERGENS_STATES.MIGHT_FREE : isMightFree;
      }
      return isMightFree;
    })
      .map((a) => a.name)
      .join(', ');
  }

  private _defineAllergensChildren(allergens: IProductAllergen[]) {
    const childrenAllergens = allergens.filter((allergen: IProductAllergen) =>
      Boolean(allergen.parentAllergen)
    );
    return allergens.map((allergen: IProductAllergen) => {
      const child = childrenAllergens.find(
        (childAllergen: IProductAllergen) =>
          childAllergen.parentAllergen === allergen.id
      );
      return {
        ...allergen,
        ...(Boolean(child) && { child }),
      };
    });
  }
}
